import React, { Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../../styles/colors';
import * as Text from '../Text';
import { FilterGroup } from './FilterGroup';
import { ProductCategory, Product } from '../../../interfaces/Product';
import { CustomerTypes, FilterListInput } from '../../..';
import { ProductCategoryList } from './ProductCategoryList';

const Headline = styled(Text.Headline2)`
	color: ${Colors.primary};
`;

const SearchDropDownBoxed = styled.div`
	max-width: 650px;
	margin: 2.5rem 0 2rem;
	z-index: 1;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: flex-start;
`;

const Filter = styled.div`
	flex-shrink: 0;
	min-width: 0;
	padding: 0 .75em 0 0;
	
	position: sticky;
	align-self: flex-end;
	bottom: 3.5rem;
`;

const SmallHeadline = styled(Text.Headline4)`
	color: ${Colors.grayDark};
	margin-bottom: .75rem;
`;

const Separator = styled.div`
	background-color: ${Colors.gray};
	height: 1px;
`;

const Content = styled.div`
	flex: 1;
	color: ${Colors.grayDark};
`;

type SetStateHook<T> = Dispatch<SetStateAction<T>>;
interface IProductOverviewPageContext {
  activeSections: string[];
  setActiveSections: SetStateHook<string[]>;
  activeTargetGroupFilters: string[];
  setActiveTargetGroupFilters: SetStateHook<string[]>;
  activeFilters: string[];
  setActiveFilters: SetStateHook<string[]>;
  filteredSections: ProductCategory[];
  setFilteredSections: SetStateHook<ProductCategory[]>;
  filterQuery: string | null;
  setFilterQuery: SetStateHook<string | null>;
}
const ProductOverviewPageContext = React.createContext<Partial<IProductOverviewPageContext>>({});
export const ProductOverviewPageContextProvider: FC = ({ children }) => {
  const [activeSections, setActiveSections] = useState<string[]>([]);
  const [activeTargetGroupFilters, setActiveTargetGroupFilters] = useState<string[]>([]);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [filteredSections, setFilteredSections] = useState<ProductCategory[]>([]);
  const [filterQuery, setFilterQuery] = useState<string | null>(null);

  const context: IProductOverviewPageContext = {
    activeSections, setActiveSections,
    activeTargetGroupFilters, setActiveTargetGroupFilters,
    activeFilters, setActiveFilters,
    filteredSections, setFilteredSections,
    filterQuery, setFilterQuery,
  }
  return (
    <ProductOverviewPageContext.Provider value={context}>
      { children }
    </ProductOverviewPageContext.Provider>
  )
}


interface Props {
  productsList: ProductCategory[];
  onProductItemClick?: (productItem: Product) => void;
}
export const ProductOverviewPage: FC<Props> = ({ productsList, ...props }) => {
  const context = useContext(ProductOverviewPageContext);
  const { activeSections, setActiveSections } = context;
  const { activeTargetGroupFilters, setActiveTargetGroupFilters } = context;
  const { activeFilters, setActiveFilters }= context;
  const { filteredSections, setFilteredSections } = context;
  const { filterQuery, setFilterQuery } = context;

  useEffect(() => {
    if ((activeTargetGroupFilters?.length || 0) < 1) {
      setActiveTargetGroupFilters([
        CustomerTypes.MAJOR_CUSTOMERS,
        CustomerTypes.BUSINESS,
        CustomerTypes.HOUSING_INDUSTRY,
      ]);
    }
    if ((activeFilters?.length || 0) < 1) {
      setActiveFilters(productsList.map(({ id }) => id));
    }
  }, [productsList]);

  const initFilteredSections = () => {
    setFilteredSections(productsList.filter((item) => activeFilters.includes(item.id)).map((category) => ({
      ...category,
      products: (category.products || []).filter(({ targetGroups, name }) => (
        targetGroups.map((targetGroup) => activeTargetGroupFilters.includes(targetGroup)).filter(Boolean).length > 0 &&   // matches target group filter
        (filterQuery === null || name?.toLowerCase().includes(filterQuery.toLowerCase()))   // matches search query
      )),
    })));
  };

  useEffect(() => {
    initFilteredSections();
  }, [activeFilters, activeTargetGroupFilters, filterQuery]);

  const availableCustomerTypes = productsList?.flatMap(({ products }) => (products || []).flatMap(({ targetGroups }) => targetGroups || []))
    .filter((item, index, array) => array.indexOf(item) === index) || [];

  const targetGroupFilterConfig = (id: string) => ({
    active: availableCustomerTypes.includes(id),
    checked: activeTargetGroupFilters.includes(id),
    onClick: () => {
      const nextFilters = activeTargetGroupFilters.filter((value) => value !== id);
      if (!activeTargetGroupFilters.includes(id)) {
        nextFilters.push(id);
      }
      setActiveTargetGroupFilters(nextFilters);
    },
  });

  return (
    <>
      <Headline>Alle Produkte auf einen Blick</Headline>

      <SearchDropDownBoxed>
        <FilterListInput
          id={'filter-products'}
          label={'Produktsuche'}
          value={filterQuery || ''}
          onChange={setFilterQuery}
          placeholder={'Produktname'}
        />
      </SearchDropDownBoxed>

      <ContentWrapper>

        <Filter>
          <SmallHeadline>Filter</SmallHeadline>
          <Separator />

          <FilterGroup
            title={'Zielgruppe'}
            items={[
              {
                title: 'Großunternehmen',
                ...targetGroupFilterConfig(CustomerTypes.MAJOR_CUSTOMERS),
              },
              {
                title: 'Gewerbe',
                ...targetGroupFilterConfig(CustomerTypes.BUSINESS),
              },
              {
                title: 'Wohnungswirtschaft',
                ...targetGroupFilterConfig(CustomerTypes.HOUSING_INDUSTRY),
              },
            ]} />

          <FilterGroup
            title={'Produktkategorien'}
            items={[
              ...productsList?.slice().sort((a, b) => a.name?.localeCompare(b.name)).map(({ id, name }, index) => ({
                title: name,
                active: true,
                checked: activeFilters.includes(id),
                onClick: () => {
                  // set active filters
                  const nextFilters = activeFilters.filter((value) => value !== id);
                  if (!activeFilters.includes(id)) {
                    nextFilters.push(id);
                  }
                  setActiveFilters(nextFilters);

                },
              })),
            ]}
          />
        </Filter>

        <Content>
          <ProductCategoryList
            productsList={productsList}
            onProductItemClick={props.onProductItemClick}
            showTechnicalName={true}
            activeSections={activeSections}
            setActiveSections={setActiveSections}
            filteredList={filteredSections} />
        </Content>

      </ContentWrapper>
    </>
  );
};
